// Runtime configuration loader — /config.json is fetched at startup so the same
// build runs in any environment (docs/design/modules/04-web-client.md §3).

export interface RuntimeConfig {
  apiBaseUrl: string;
  hubUrl: string;
  mediaWsBaseUrl: string;
  maxVideoBitrate: number;
  bufferChunkMs: number;
  bufferWindowSec: number;
  bufferTtlHours: number;
  mediaSecurityMode: "hopByHop" | "strictE2EE";
  locale: RuntimeLocale;
  /**
   * Optional demo-only admin entry path (e.g. "/demo/admin"). When set, opening /admin
   * without an admin-authorized token redirects here to mint a fresh demo admin token.
   * Leave unset in production so direct /admin stays protected (shows the Hebrew auth notice).
   */
  adminDemoEntryUrl?: string;
}

export type RuntimeLocale = "auto" | "he" | "en";

let cached: RuntimeConfig | null = null;

const defaultConfig: RuntimeConfig = {
  apiBaseUrl: "/api/v1",
  hubUrl: "/hubs/call",
  mediaWsBaseUrl: "/media-ws",
  maxVideoBitrate: 900000,
  bufferChunkMs: 4000,
  bufferWindowSec: 150,
  bufferTtlHours: 24,
  mediaSecurityMode: "hopByHop",
  locale: "auto",
  adminDemoEntryUrl: "/demo/admin"
};

export async function loadConfig(): Promise<RuntimeConfig> {
  if (cached) return cached;
  try {
    const res = await fetch("/config.json", { cache: "no-store" });
    if (res.ok) {
      cached = (await res.json()) as RuntimeConfig;
      return cached;
    }
    console.warn("/config.json fetch failed: " + res.status + "; using same-origin defaults");
  } catch (err) {
    console.warn("/config.json fetch failed; using same-origin defaults", err);
  }
  cached = defaultConfig;
  return cached;
}

export function getConfig(): RuntimeConfig {
  if (!cached) throw new Error("config not loaded — call loadConfig() before getConfig()");
  return cached;
}
